import type { PromoSlot as PromoSlotId, PromoView } from "@bing/shared";
import { useEffect, useState, type ReactNode } from "react";
import { useSession } from "../store/context";

/** Small honesty/AT label so a managed promo never masquerades as app content. */
const PROMO_LABEL = "پیشنهاد ویژه";

/**
 * ONE reusable promo surface, mounted at every slot site (lobby hero, secondary
 * strip, splash, empty states, post-game). It asks the server for the active
 * promo for its `slot` and:
 *
 *  - renders **nothing** when the response is null — the case TODAY, since the
 *    `promos` flag is off and there are no campaigns. Critically, state only ever
 *    changes when a promo EXISTS, so in the null case there is zero re-render,
 *    zero DOM output, and no visual change — just one cheap GET that returns null;
 *  - when a promo exists, renders a tasteful card (image / title / body / CTA)
 *    built from the app's design tokens, fires an impression on view, and records
 *    a click when the CTA is tapped (the CTA is a real link that follows `ctaHref`).
 *
 * Guarded against setting state after unmount. Fetch/record failures are
 * swallowed (a promo must never break a screen).
 */
export function PromoSlot({
  slot,
  className,
}: {
  slot: PromoSlotId;
  className?: string;
}): ReactNode {
  const api = useSession((s) => s.api);
  const [promo, setPromo] = useState<PromoView | null>(null);

  useEffect(() => {
    let active = true;
    api
      .getPromo(slot)
      .then((p) => {
        // ONLY touch state when there IS a promo — the null path is a true no-op.
        if (active && p) {
          setPromo(p);
          // Impression on view (fire-and-forget; never awaited on a render path).
          void api.recordPromoImpression(p.id).catch(() => undefined);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [slot, api]);

  if (!promo) return null;

  const onClick = (): void => {
    void api.recordPromoClick(promo.id).catch(() => undefined);
  };

  return (
    <aside
      dir="rtl"
      aria-label={PROMO_LABEL}
      data-promo-slot={slot}
      className={`overflow-hidden rounded-xl border border-gold-deep/25 bg-surface-container/60 text-right shadow-[0_8px_28px_rgba(0,0,0,0.28)] ${
        className ?? ""
      }`}
    >
      {promo.imageRef ? (
        <img
          src={promo.imageRef}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-28 w-full object-cover"
        />
      ) : null}
      <div className="flex flex-col gap-1.5 p-4">
        <span className="font-caption text-caption text-on-surface-variant/80">{PROMO_LABEL}</span>
        {promo.title ? (
          <h3 className="m-0 font-headline-sm text-headline-sm text-gold-deep">{promo.title}</h3>
        ) : null}
        {promo.body ? (
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">{promo.body}</p>
        ) : null}
        {promo.ctaHref ? (
          <a
            href={promo.ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            className="mt-1 inline-flex w-fit cursor-pointer items-center rounded-lg bg-gold-deep px-4 py-2 font-label-md text-label-md text-ink-on-gold no-underline shadow-[0_0_15px_rgba(199,154,63,0.3)] transition-all hover:brightness-110 active:scale-95"
          >
            {promo.ctaText ?? PROMO_LABEL}
          </a>
        ) : null}
      </div>
    </aside>
  );
}
